import { JsonRpcErrorCodes } from './types';
import { getToolsForHost } from './tools';
export class McpServer {
    tools = new Map();
    initialized = false;
    constructor(hostType) {
        const toolDefs = getToolsForHost(hostType);
        for (const def of toolDefs) {
            this.tools.set(def.tool.name, def);
        }
    }
    async processMessage(message) {
        try {
            const parsed = JSON.parse(message);
            if (this.isJsonRpcRequest(parsed)) {
                const response = await this.handleRequest(parsed);
                return JSON.stringify(response);
            }
            if (this.isJsonRpcNotification(parsed)) {
                return null;
            }
            return JSON.stringify(this.createErrorResponse(null, JsonRpcErrorCodes.INVALID_REQUEST, 'Invalid JSON-RPC message'));
        }
        catch (error) {
            console.error('MCP parse error:', error);
            return JSON.stringify(this.createErrorResponse(null, JsonRpcErrorCodes.PARSE_ERROR, 'Parse error'));
        }
    }
    async handleRequest(request) {
        try {
            switch (request.method) {
                case 'initialize':
                    return this.handleInitialize(request);
                case 'tools/list':
                    return this.handleToolsList(request);
                case 'tools/call':
                    return await this.handleToolsCall(request);
                case 'ping':
                    return { jsonrpc: '2.0', id: request.id, result: {} };
                default:
                    return this.createErrorResponse(request.id, JsonRpcErrorCodes.METHOD_NOT_FOUND, `Method '${request.method}' not found`);
            }
        }
        catch (error) {
            console.error('Error handling MCP request:', error);
            return this.createErrorResponse(request.id, JsonRpcErrorCodes.INTERNAL_ERROR, error instanceof Error ? error.message : 'Internal error');
        }
    }
    handleInitialize(request) {
        if (!request.params || !this.isInitializeParams(request.params)) {
            return this.createErrorResponse(request.id, JsonRpcErrorCodes.INVALID_PARAMS, 'Invalid initialize parameters');
        }
        const params = request.params;
        const supportedVersions = ['2024-11-05', '2025-06-18', '2025-11-25'];
        if (!supportedVersions.includes(params.protocolVersion)) {
            return this.createErrorResponse(request.id, JsonRpcErrorCodes.INVALID_PARAMS, `Unsupported protocol version: ${params.protocolVersion}`);
        }
        this.initialized = true;
        const result = {
            protocolVersion: params.protocolVersion,
            capabilities: {
                tools: { listChanged: true },
            },
            serverInfo: {
                name: 'Airia Office Add-in MCP Server',
                version: '1.0.0',
            },
        };
        return { jsonrpc: '2.0', id: request.id, result };
    }
    handleToolsList(request) {
        if (!this.initialized) {
            return this.createErrorResponse(request.id, JsonRpcErrorCodes.INTERNAL_ERROR, 'Server not initialized');
        }
        const tools = Array.from(this.tools.values()).map((def) => def.tool);
        return { jsonrpc: '2.0', id: request.id, result: { tools } };
    }
    async handleToolsCall(request) {
        if (!this.initialized) {
            return this.createErrorResponse(request.id, JsonRpcErrorCodes.INTERNAL_ERROR, 'Server not initialized');
        }
        if (!request.params || !this.isToolsCallParams(request.params)) {
            return this.createErrorResponse(request.id, JsonRpcErrorCodes.INVALID_PARAMS, 'Invalid tools/call parameters');
        }
        const { name, arguments: args } = request.params;
        const toolDef = this.tools.get(name);
        if (!toolDef) {
            return this.createErrorResponse(request.id, JsonRpcErrorCodes.INVALID_PARAMS, `Tool '${name}' not found`);
        }
        try {
            const result = await toolDef.execute(args);
            return { jsonrpc: '2.0', id: request.id, result };
        }
        catch (error) {
            const message = error instanceof Error ? error.message : 'Tool execution failed';
            return {
                jsonrpc: '2.0',
                id: request.id,
                result: {
                    isError: true,
                    content: [{ type: 'text', text: `Error: ${message}` }],
                },
            };
        }
    }
    createErrorResponse(id, code, message) {
        return {
            jsonrpc: '2.0',
            id: id ?? null,
            error: { code, message },
        };
    }
    isJsonRpcRequest(obj) {
        return (typeof obj === 'object' &&
            obj !== null &&
            'jsonrpc' in obj &&
            obj.jsonrpc === '2.0' &&
            'id' in obj &&
            'method' in obj &&
            typeof obj.method === 'string');
    }
    isJsonRpcNotification(obj) {
        return (typeof obj === 'object' &&
            obj !== null &&
            'jsonrpc' in obj &&
            obj.jsonrpc === '2.0' &&
            'method' in obj &&
            typeof obj.method === 'string' &&
            !('id' in obj));
    }
    isInitializeParams(obj) {
        return (typeof obj === 'object' &&
            obj !== null &&
            'protocolVersion' in obj &&
            'capabilities' in obj &&
            'clientInfo' in obj);
    }
    isToolsCallParams(obj) {
        return (typeof obj === 'object' &&
            obj !== null &&
            'name' in obj &&
            typeof obj.name === 'string' &&
            'arguments' in obj &&
            typeof obj.arguments === 'object' &&
            obj.arguments !== null &&
            !Array.isArray(obj.arguments));
    }
    isInitialized() {
        return this.initialized;
    }
}
